import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import type { AxiosError } from 'axios'
import type { UpdateGuideInput } from '@/types/guide'
import { GuidesService } from '@/services/guides'
import TitleHeader from '@/components/TitleHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/RichTextEditor'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getTranslation } from '@/lib/utils'

export const Route = createFileRoute(
  '/_authenticated/products/guides/update/$guideId',
)({
  component: UpdateGuide,
})

function UpdateGuide() {
  const { guideId } = Route.useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('vi')

  // Vietnamese fields
  const [viName, setViName] = useState('')
  const [viDescription, setViDescription] = useState('')

  // English fields
  const [enName, setEnName] = useState('')
  const [enDescription, setEnDescription] = useState('')

  // Fetch guide details
  const {
    data: guideData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['guide', guideId],
    queryFn: () => GuidesService.getById(guideId),
  })

  // Populate form when data is loaded
  useEffect(() => {
    if (guideData?.data) {
      const guide = guideData.data
      const translations = guide.translations

      const viTranslation = getTranslation(translations, 'name', 'vi')
      const viDesc = getTranslation(translations, 'description', 'vi')
      const enTranslation = getTranslation(translations, 'name', 'en')
      const enDesc = getTranslation(translations, 'description', 'en')

      setViName(viTranslation || '')
      setViDescription(viDesc || '')
      setEnName(enTranslation || '')
      setEnDescription(enDesc || '')
    }
  }, [guideData])

  const mutation = useMutation({
    mutationFn: (payload: UpdateGuideInput) =>
      GuidesService.update(guideId, payload),
    onSuccess: () => {
      toast.success('Cập nhật hướng dẫn thành công')
      navigate({ to: '/products/guides' })
    },
    onError: (err: AxiosError) => {
      toast.error(
        (err.response?.data as any)?.message || 'Cập nhật hướng dẫn thất bại',
      )
    },
  })

  const handleSubmit = () => {
    if (!viName || !viDescription) {
      toast.error('Vui lòng điền đầy đủ thông tin tiếng Việt')
      return
    }

    const translations: UpdateGuideInput['translations'] = [
      {
        language: 'vi',
        name: viName,
        description: viDescription,
      },
    ]

    // Add English translation if provided
    if (enName || enDescription) {
      translations.push({
        language: 'en',
        name: enName || viName,
        description: enDescription || viDescription,
      })
    }

    mutation.mutate({
      translations,
    })
  }

  const handleCancel = () => {
    navigate({ to: '/products/guides' })
  }

  const isFormValid = viName && viDescription

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin w-8 h-8 text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500 mb-4">Không thể tải thông tin hướng dẫn</p>
          <Button onClick={handleCancel} variant="outline">
            Quay lại
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <TitleHeader
        title="Sửa hướng dẫn"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              Huỷ
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmit}
              disabled={mutation.isPending || !isFormValid}
            >
              {mutation.isPending ? 'Đang lưu...' : 'Lưu'}
            </Button>
          </div>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="vi">Tiếng Việt</TabsTrigger>
          <TabsTrigger value="en">English</TabsTrigger>
        </TabsList>

        <TabsContent value="vi" className="space-y-6">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Hướng dẫn</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="vi-name" className="mb-2 inline-block">
                    Tiêu đề hướng dẫn
                  </Label>
                  <Input
                    id="vi-name"
                    value={viName}
                    onChange={(e) => setViName(e.target.value)}
                    placeholder="Hướng dẫn game...."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Nội dung</CardTitle>
            </CardHeader>
            <CardContent>
              <RichTextEditor
                value={viDescription}
                onChange={setViDescription}
                placeholder="Nhập nội dung hướng dẫn..."
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="en" className="space-y-6">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="en-name" className="mb-2 inline-block">
                    Guide Title
                  </Label>
                  <Input
                    id="en-name"
                    value={enName}
                    onChange={(e) => setEnName(e.target.value)}
                    placeholder="Game guide...."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Content</CardTitle>
            </CardHeader>
            <CardContent>
              <RichTextEditor
                value={enDescription}
                onChange={setEnDescription}
                placeholder="Enter guide content..."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
